import React, { useState } from "react";
import {
  LackOfProsecutionCaseSearchResult,
  LackOfProsecutionCaseSearchResponse,
} from "../LackOfProsecutionTypes";
import { Http } from "utils/Http";
import {
  Modal,
  Row,
  Button,
  Col,
  DataTableColumn,
  Icon,
  DataTable,
  HelpIconMode,
} from "@countysuite/ui";
import { TsCaseSearch } from "components";
import { HelpIconButton } from "components/HelpIconButton/HelpIconButton";

export type Props = {
  onHide: () => void;
  onAddCases: (addedCases: LackOfProsecutionCaseSearchResult[]) => void;
  show: boolean;
};

export const LackOfProsecutionAddCaseModal: React.FC<Props> = (props) => {
  const [searchResults, setSearchResults] = useState<
    LackOfProsecutionCaseSearchResult[]
  >([]);
  const [addedCases, setAddedCases] = useState<
    LackOfProsecutionCaseSearchResult[]
  >([]);

  function resetState() {
    setAddedCases([]);
    setSearchResults([]);
  }

  function handleHide() {
    resetState();
    props.onHide();
  }

  function onChooseCase(selectedCase: LackOfProsecutionCaseSearchResult) {
    if (
      selectedCase &&
      addedCases.filter((c) => c.caseID === selectedCase.caseID).length === 0
    ) {
      setAddedCases(function (prevCases) {
        return [...prevCases, selectedCase];
      });
    } else {
      alert("Case " + selectedCase.caseNumber + " is already in the list.");
    }
  }

  function onRemoveCase(caseId: number) {
    setAddedCases(function (prevCases) {
      let index = prevCases.findIndex((c) => c.caseID === caseId);
      if (index > -1) {
        return [...prevCases.slice(0, index), ...prevCases.slice(index + 1)];
      } else {
        return [...prevCases];
      }
    });
  }

  async function typeaheadSearch(searchText: string) {
    const data = { caseNumber: searchText };
    const apiResponse = await Http.get<LackOfProsecutionCaseSearchResponse>({
      url: "LackOfProsecution/SearchCaseNumbers",
      data,
    });
    const results = apiResponse.results || [];
    setSearchResults(results);
    return results;
  }

  async function onFinish() {
    await props.onAddCases(addedCases);
    resetState();
    props.onHide();
  }

  const dtColumns: DataTableColumn<LackOfProsecutionCaseSearchResult>[] = [
    {
      Header: "Case Number",
      width: 140,
      id: "caseNumberCol",
      accessor: (row) => row.caseNumber,
    },
    {
      Header: "Caption",
      id: "caseCaptionCol",
      style: { whiteSpace: "normal" },
      accessor: (row) => row.caseCaption,
    },
    {
      Header: "Category",
      id: "caseCategoryCol",
      style: { whiteSpace: "normal" },
      accessor: (row) => row.caseCategoryName,
    },
    {
      Header: "",
      width: 40,
      id: "actionCol",
      Cell: (cell) => {
        return (
          <Button
            color="danger"
            onClick={() => onRemoveCase(cell.original.caseID)}
          >
            <Icon glyph="trash" />
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      size="large"
      onHide={handleHide}
      show={props.show}
      title="Add Additional Cases"
      buttons={() => (
        <>
          <HelpIconButton
            mode={HelpIconMode.Modal}
            style={{ marginRight: "6px" }}
          >
            <p>
              Search for additional cases using the box provided below and click
              on them. Click finish when you are done adding cases to save your
              changes.
            </p>
          </HelpIconButton>
          <Button
            color="success"
            onClick={onFinish}
            disabled={addedCases.length <= 0}
          >
            Finish
          </Button>
          <Button onClick={handleHide}>Cancel</Button>
        </>
      )}
    >
      <Row>
        <Col xs={12}>
          <DataTable
            data={addedCases}
            columns={dtColumns}
            showPaginationBottom={true}
            showPaginationTop={false}
            defaultPageSize={10}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <div
            style={{
              marginBottom: "1em",
              marginLeft: "3em",
              marginRight: "3em",
              textAlign: "center",
            }}
          >
            <TsCaseSearch
              id="caseNumber"
              onSearch={typeaheadSearch}
              options={searchResults}
              onChange={onChooseCase}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
};
